import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import { formatLatexPreview } from "../../../../components/inputs/FractionInput";

// ==== FORMATOWANIE FRAKCJI ====

function formatFraction(numerator, denominator) {
  const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));

  const sign = Math.sign(numerator) * Math.sign(denominator);
  const absNum = Math.abs(numerator);
  const absDen = Math.abs(denominator);
  const d = gcd(absNum, absDen);

  const simpNum = absNum / d;
  const simpDen = absDen / d;

  if (simpNum % simpDen === 0) {
    return `${sign * (simpNum / simpDen)}`;
  }

  const whole = Math.floor(simpNum / simpDen);
  const remainder = simpNum % simpDen;

  if (whole === 0) {
    return `${sign < 0 ? "-" : ""}\\dfrac{${simpNum}}{${simpDen}}`;
  }

  return `${sign < 0 ? "-" : ""}${whole}\\dfrac{${remainder}}{${simpDen}}`;
}

function formatLinearLatex(aLatex, bLatex, bValue) {
  const aPart = aLatex === "1" ? "x" : aLatex === "-1" ? "-x" : `${aLatex}x`;

  if (bValue === 0) {
    return `f(x) = ${aPart}`;
  }

  return `f(x) = ${aPart} ${bValue >= 0 ? "+" : "-"} ${bLatex.replace("-", "")}`;
}

function formatLinearLatexFromUser(aParsed, aRaw, bParsed, bRaw) {
  if (!Number.isFinite(aParsed) || !Number.isFinite(bParsed)) return null;

  const aLatex = formatLatexPreview(aRaw || "");
  const bLatex = formatLatexPreview(bRaw || "");
  return formatLinearLatex(aParsed === 1 ? "1" : aParsed === -1 ? "-1" : aLatex, bLatex, bParsed);
}


// ==== GENERATOR ====

export function generateTask() {
  const rand = () => Math.floor(Math.random() * 17) - 8;
  let x1, y1, x2, y2;

  do {
    x1 = rand();
    y1 = rand();
    x2 = rand();
    y2 = rand();
  } while (x1 === x2); // unikamy dzielenia przez 0

  const dx = x2 - x1;
  const dy = y2 - y1;

  const gcdSlope = (a, b) => (b === 0 ? Math.abs(a) : gcdSlope(b, a % b));
  const slopeGcd = gcdSlope(dy, dx);
  const simpNum = dy / slopeGcd;
  const simpDen = dx / slopeGcd;

  const bNum = y1 * simpDen - simpNum * x1;
  const bDen = simpDen;
  const bGcd = gcdSlope(bNum, bDen);
  const bSimpNum = bNum / bGcd;
  const bSimpDen = bDen / bGcd;

  return {
    type: "LinearFromTwoPointsTask",
    data: {
      x1, y1, x2, y2,
      slope: { numerator: dy, denominator: dx },
      b: { numerator: bSimpNum, denominator: bSimpDen }
    }
  };
}


// ==== TRESC ====

export function renderTask({ data }) {
  const { x1, y1, x2, y2 } = data;

  return (
    <>
      <p>
        Wyznacz wzór funkcji liniowej <InlineMath math="f(x)=ax+b" />, która przechodzi przez punkty:
      </p>
      <p>
        <InlineMath math={`A=(${x1},${y1})`} />,{" "}
        <InlineMath math={`B=(${x2},${y2})`} />
      </p>
      <p>
        W szczególności oblicz współczynnik kierunkowy <InlineMath math="a" /> oraz wyraz wolny <InlineMath math="b" />.
      </p>
    </>
  );
}


// ==== INPUT ====

export function renderInput(_, userAnswer, onChange, disabled) {
  const ua = userAnswer ?? {};

  return (
    <div className="flex gap-4 items-center flex-wrap">
      <div className="flex items-center gap-2">
        <span><InlineMath math="a = " /></span>
        <FractionInput
          value={ua.answerA || ""}
          onChange={(val) => onChange({ ...ua, answerA: val })}
          disabled={disabled}
          label="a"
        />
      </div>
      <div className="flex items-center gap-2">
        <span><InlineMath math="b = " /></span>
        <FractionInput
          value={ua.answerB || ""}
          onChange={(val) => onChange({ ...ua, answerB: val })}
          disabled={disabled}
          label="b"
        />
      </div>
    </div>
  );
}


// ==== CHECK ====

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;

  const aUser = userAnswer.answerA?.parsed;
  const bUser = userAnswer.answerB?.parsed;
  if (aUser == null || bUser == null) return false;

  const aExpected = data.slope.numerator / data.slope.denominator;
  const bExpected = data.b.numerator / data.b.denominator;

  return Math.abs(aExpected - aUser) < 0.01 && Math.abs(bExpected - bUser) < 0.01;
}


// ==== PODGLAD ODPOWIEDZI UCZNIA ====

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    !userAnswer.answerA?.raw ||
    !userAnswer.answerB?.raw
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const aValue = userAnswer.answerA?.parsed;
  const bValue = userAnswer.answerB?.parsed;
  const userFormulaLatex = formatLinearLatexFromUser(
    aValue,
    userAnswer.answerA.raw,
    bValue,
    userAnswer.answerB.raw
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <div><InlineMath math={formatLatexPreview(userAnswer.answerA.raw, "a")} /></div>
      <div><InlineMath math={formatLatexPreview(userAnswer.answerB.raw, "b")} /></div>
      {userFormulaLatex ? (
        <div style={{ marginTop: "0.25rem" }}>
          <InlineMath math={userFormulaLatex} />
        </div>
      ) : null}
    </div>
  );
}


// ==== POPRAWNA ODPOWIEDZ ====

export function renderAnswer({ data }) {
  const { numerator, denominator } = data.slope;
  const { numerator: bNum, denominator: bDen } = data.b;

  const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));

  const slopeGcd = gcd(numerator, denominator);
  const simpNum = numerator / slopeGcd;
  const simpDen = denominator / slopeGcd;
  const aLatex = formatFraction(simpNum, simpDen);

  const b = bNum / bDen;
  const bLatex = formatFraction(bNum, bDen);

  return (
    <div className="space-y-4 mt-4" style={{ textAlign: "left" }}>
      <div>
        <InlineMath math={`a = ${aLatex}`} />
      </div>
      <div>
        <InlineMath math={`b = ${bLatex}`} />
      </div>

      <strong>Wzor funkcji ma postac:</strong>
      <div style={{ display: "block", textAlign: "left", marginTop: "0.5rem" }}>
        <InlineMath math={`\\displaystyle ${formatLinearLatex(aLatex, bLatex, b)}`} />
      </div>
    </div>
  );
}


// ==== WYJASNIENIE ====

export function renderExplanation({ data }) {
  const { x1, y1, x2, y2 } = data;

  const dx = x2 - x1;
  const dy = y2 - y1;

  const fs = (v) => (v < 0 ? `(${v})` : v);

  const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));
  const d = gcd(dy, dx);
  const simpNum = dy / d;
  const simpDen = dx / d;
  const aLatex = formatFraction(simpNum, simpDen);

  const bNum = y1 * simpDen - simpNum * x1;
  const bDen = simpDen;
  const bGcd = gcd(bNum, bDen);
  const bSimpNum = bNum / bGcd;
  const bSimpDen = bDen / bGcd;
  const b = bSimpNum / bSimpDen;
  const bLatex = formatFraction(bSimpNum, bSimpDen);

  const axNum = simpNum * x1;
  const axDen = simpDen;
  const axGcd = gcd(axNum, axDen);
  const axSimpNum = axNum / axGcd;
  const axSimpDen = axDen / axGcd;
  const axLatex = formatFraction(axSimpNum, axSimpDen);

  return (
    <div className="space-y-3">
      <p><strong>Krok 1:</strong> Obliczamy wspolczynnik kierunkowy funkcji na podstawie dwoch punktow:</p>
      <BlockMath math="a = \dfrac{y_2 - y_1}{x_2 - x_1}" />
      <BlockMath math={`a = \\dfrac{${fs(y2)} - ${fs(y1)}}{${fs(x2)} - ${fs(x1)}} = \\dfrac{${dy}}{${dx}} = ${aLatex}`} />

      <p><strong>Krok 2:</strong> Obliczamy wyraz wolny <InlineMath math="b" /> ze wzoru:</p>
      <BlockMath math="b = y - ax" />
      <p>Podstawiamy wspolrzedne punktu <InlineMath math={`A = (${x1}, ${y1})`} /> oraz <InlineMath math={`a = ${aLatex}`} />:</p>
      <BlockMath
        math={`b = ${fs(y1)} - (${aLatex} \\cdot ${fs(x1)}) = ${fs(y1)} - ${axNum < 0 ? `(${axLatex})` : axLatex} = ${bLatex}`}
      />

      <p><strong>Krok 3:</strong> Zapisujemy wzor funkcji:</p>
      <BlockMath math={formatLinearLatex(aLatex, bLatex, b)} />
    </div>
  );
}


export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
